import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

// A single, quiet editorial module used in two places:
//   - Homepage: "Latest Perspective" (variant="latest")
//   - Technology foundation: "Featured Perspective" (variant="featured")
// The card is intentionally borderless and image-forward, so it reads as an
// editorial insert rather than a promotional card.
export const FeaturedPerspectiveCard = ({
  eyebrow,
  categoryLabel,
  title,
  subtitle,
  href,
  linkLabel,
  hero,             // { image, alt } or null
  heroPlaceholder,  // { label, caption }
  isDraft = false,
  testId,
}) => {
  return (
    <section className="max-w-7xl mx-auto px-6 md:px-12 py-16" data-testid={testId}>
      <div className="border-t border-b border-subtle-custom py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Left: editorial image or placeholder */}
          <div className="lg:col-span-6">
            <div className="aspect-[16/10] overflow-hidden relative bg-surface-custom">
              {hero?.image ? (
                <img src={hero.image} alt={hero.alt || title} loading="lazy" className="w-full h-full object-cover" />
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#141210] text-[var(--accent-bronze)]/80" data-testid={`${testId}-hero-placeholder`}>
                  <span className="text-[9px] font-sans tracking-[0.35em] uppercase">{heroPlaceholder.label}</span>
                  <span className="mt-3 w-10 h-px bg-[var(--accent-bronze)]/60" />
                  <span className="mt-3 text-[10px] font-serif italic text-[var(--accent-bronze)]/60 max-w-[22ch] text-center px-6">{heroPlaceholder.caption}</span>
                </div>
              )}
            </div>
          </div>

          {/* Right: editorial copy */}
          <div className="lg:col-span-6 space-y-6">
            <div className="flex items-center gap-3 text-[10px] font-sans tracking-[0.35em] uppercase text-[var(--accent-bronze)] font-semibold">
              <span>{eyebrow}</span>
              <span className="w-6 h-px bg-[var(--accent-bronze)]/60" />
              <span>{categoryLabel}</span>
              {isDraft && <><span className="w-6 h-px bg-[var(--accent-bronze)]/60" /><span className="text-secondary-custom/70 font-normal tracking-[0.25em]">Draft</span></>}
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif tracking-tight text-primary-custom leading-[1.1]">{title}</h2>
            <p className="font-serif text-base md:text-lg text-secondary-custom leading-relaxed font-light max-w-prose">{subtitle}</p>
            <div className="pt-4">
              <Link
                to={href}
                data-testid={`${testId}-link`}
                className="group inline-flex items-center gap-3 text-[11px] font-sans tracking-[0.3em] uppercase text-primary-custom font-semibold hover:text-[var(--accent-bronze)] transition-colors"
              >
                <span>{linkLabel}</span>
                <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
